import {Icon, Tooltip, view as deprecatedView} from '@sail/ui/deprecated';
import {view} from '@sail/ui';
import {warning} from '@sail/icons/react/Icon';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../FeatureFlagsProvider';
import {ChatRowContainer} from './ChatRowContainer';
import type {TextBubble} from './TextBubble';

type Props = {
  position?: 'start' | 'end';
  avatar?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
  messageType?: React.ComponentProps<typeof TextBubble>['messageType'];
};

const ErrorMessage = ({children}: {children: React.ReactNode}): JSX.Element => {
  return (
    <Tooltip
      placement="left"
      delay={0}
      trigger={
        <Icon
          icon={warning}
          aria-label="warning"
          color="red"
          data-testid="error-tooltip-icon"
        />
      }
    >
      {children}
    </Tooltip>
  );
};

export const ChatRow = ({
  children,
  position = 'start',
  avatar,
  error,
  messageType,
}: Props): JSX.Element => {
  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();

  if (useNewDesign) {
    return (
      // eslint-disable-next-line no-use-before-define
      <ChatRowNext position={position} error={error} messageType={messageType}>
        {children}
      </ChatRowNext>
    );
  }

  const innerNodes = [
    error ? <ErrorMessage key="error">{error}</ErrorMessage> : null,
    avatar ? (
      <deprecatedView.div key="avatar">{avatar}</deprecatedView.div>
    ) : null,
    children,
  ];

  const largeMargin = useDotComDesign ? 'xxlarge' : 'large';

  return (
    <deprecatedView.div
      data-testid="chat-row"
      css={{
        marginLeft: position === 'start' ? 0 : largeMargin,
        marginRight: position === 'start' ? largeMargin : 0,
      }}
    >
      <ChatRowContainer position={position}>
        {position === 'start' ? innerNodes : innerNodes.reverse()}
      </ChatRowContainer>
    </deprecatedView.div>
  );
};

const ChatRowNext = ({
  children,
  position = 'start',
  error,
  messageType,
}: Omit<Props, 'avatar'>): JSX.Element => {
  const errorNode = error ? (
    <ErrorMessage key="error">{error}</ErrorMessage>
  ) : null;

  return (
    <view.div
      className="uh-conversations-chat-row"
      data-testid="chat-row"
      data-message={messageType}
      css={{
        stack: 'x',
        alignX: position,
        gap: 'small',
        marginLeft: position === 'start' ? 0 : 'large',
      }}
    >
      {position === 'start' ? errorNode : null}
      {children}
      {position !== 'start' ? errorNode : null}
    </view.div>
  );
};
